import axios from 'axios';
import { getCurrentChatUserScope, setCurrentChatUserScope } from '../../utils/ai/chatSessionStorage';

const AI_BASE_URL = process.env.REACT_APP_QMETRIX_AI_URL || 'http://127.0.0.1:8000';
const CHAT_ENDPOINT = `${AI_BASE_URL}/api/v1/ai/chat`;
const CHAT_TIMEOUT_MS = Number(process.env.REACT_APP_QMETRIX_AI_CHAT_TIMEOUT_MS) || 60000;

const createResponse = (content, extras = {}) => ({
  id: `assistant-${Date.now()}`,
  role: 'assistant',
  content,
  timestamp: new Date().toISOString(),
  ...extras,
});

const getSessionValue = (key) => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.sessionStorage.getItem(key);
};

const buildRequestHeaders = () => {
  const token = getSessionValue('qmetrix-token');
  return token ? { 'qmetrix-token': token } : {};
};

const getStoredUserData = () => {
  const rawUserData = getSessionValue('userData');
  if (!rawUserData) {
    return null;
  }

  try {
    return JSON.parse(rawUserData);
  } catch (error) {
    return null;
  }
};

const getCurrentUserScope = (appState = {}) => {
  const loginUser = appState?.login?.data || {};
  const sessionUser = getStoredUserData() || {};
  const scopeCandidate =
    loginUser?.email ||
    sessionUser?.email ||
    loginUser?.name ||
    sessionUser?.name ||
    getSessionValue('companyId') ||
    appState?.user?.data?.email;
  const scope = String(scopeCandidate || 'anonymous').trim().toLowerCase();
  const normalizedScope = scope || 'anonymous';
  setCurrentChatUserScope(normalizedScope);
  return getCurrentChatUserScope();
};

const formatSessionDate = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  try {
    return parsed.toLocaleDateString('en-IN');
  } catch (error) {
    return String(value);
  }
};

const getOrCreateBrowserSessionId = () => {
  const storageKey = 'qmetrix-ai-browser-session-id';
  const existingId = getSessionValue(storageKey);
  if (existingId) {
    return existingId;
  }

  const nextId = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  window.sessionStorage.setItem(storageKey, nextId);
  return nextId;
};

const buildConversationSessionId = (appState = {}) => {
  const jira = appState?.jira || {};
  const userScope = getCurrentUserScope(appState);
  const parts = [
    getOrCreateBrowserSessionId(),
    userScope,
    getSessionValue('companyId') || jira.selectedOrgId || 'no-company',
    getSessionValue('projectId') || jira.selectedProjectId || 'no-project',
    getSessionValue('boardId') || jira.boardList?.[0]?.boardId || 'no-board',
    getSessionValue('sprintId') || jira.selectedSprintId || 'no-sprint',
    getSessionValue('releaseId') || jira.selectedReleaseId || 'no-release',
    getSessionValue('repo') || jira.repoList?.[0]?.name || 'no-repo',
  ];

  return parts.join(':');
};

const compactValue = (value, depth = 0) => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  if (depth > 4) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value.slice(0, 8).map((item) => compactValue(item, depth + 1)).filter(Boolean);
  }

  if (typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, childValue]) => {
      const compacted = compactValue(childValue, depth + 1);
      if (compacted !== undefined && !(Array.isArray(compacted) && compacted.length === 0)) {
        acc[key] = compacted;
      }
      return acc;
    }, {});
  }

  return value;
};

const pickFirst = (...values) => values.find((value) => value !== null && value !== undefined && value !== '');

const buildTechQualityWidgetSnapshot = (widget = {}) => {
  const metric = widget?.metric || {};
  return compactValue({
    currentValue: pickFirst(metric.lifeTime, metric.lifetime, metric.value),
    trend: pickFirst(metric.trend, metric.delta),
    trendStatus: pickFirst(metric.status, metric.trendStatus),
    target: pickFirst(metric.target),
    highest: pickFirst(metric.high, metric.highest),
    lowest: pickFirst(metric.low, metric.lowest),
    currentYear: {
      highest: pickFirst(metric.high, metric.highest),
      lowest: pickFirst(metric.low, metric.lowest),
    },
  });
};

const resolveBoardId = (appState = {}) => {
  const sessionBoardId = getSessionValue('boardId');
  if (sessionBoardId) {
    return sessionBoardId;
  }

  const jira = appState?.jira || {};
  const projectId = getSessionValue('projectId') || jira.selectedProjectId;
  const boardList = jira.boardList || jira.boardListByProjectId?.[projectId] || [];
  const matchedBoard = boardList.find(
    (board) => String(board?.projectId || '') === String(projectId || ''),
  );

  return matchedBoard?.boardId || boardList?.[0]?.boardId || null;
};

const buildDashboardContext = (appState = {}) => {
  const ids = {
    companyId: getSessionValue('companyId'),
    projectId: getSessionValue('projectId'),
    boardId: resolveBoardId(appState),
    sprintId: getSessionValue('sprintId'),
    releaseId: getSessionValue('releaseId'),
    repo: getSessionValue('repo'),
  };
  const jira = appState?.jira || {};
  const cxo = appState?.cxo || {};
  const git = appState?.git || {};
  const techQuality = appState?.techQuality || {};
  const velocityData = appState?.velocityData?.data;
  const releaseDashboard = appState?.releaseDashboard || {};
  const releaseDashboardData = releaseDashboard?.data || {};
  const releaseDashboardMeta = releaseDashboard?.lastFetchedFor || {};
  const techQualitySnapshot = compactValue({
    selectedSeverity: techQuality?.selectedSeverity || 'All',
    bugRate: buildTechQualityWidgetSnapshot(techQuality?.data?.bugRate),
    timeToResolution: compactValue({
      ...buildTechQualityWidgetSnapshot(techQuality?.data?.TimeToResolution || techQuality?.data?.timeToResolution),
      selectedSeverity: techQuality?.selectedSeverity || 'All',
    }),
    defectEscapeRatio: buildTechQualityWidgetSnapshot(techQuality?.data?.defectEscapeRatio),
    defectAcceptanceRatio: buildTechQualityWidgetSnapshot(techQuality?.data?.defectAcceptanceRatio),
  });
  const boardList = jira.boardList || jira.boardListByProjectId?.[jira.selectedProjectId] || [];
  const sprintList = jira.sprintList || [];
  const releasesList = jira.releasesList || [];
  const selectedValue = jira.selectedValue || getSessionValue('typeValue') || 'sprint';
  const selectedSprintFromList = sprintList.find((sprint) => String(sprint?._id) === String(jira.selectedSprintId));
  const selectedSprint = jira.Sprint || selectedSprintFromList || {};
  const selectedSprintStartDate =
    formatSessionDate(selectedSprint?.startDate || getSessionValue('sprintStartDate'));
  const selectedSprintEndDate =
    formatSessionDate(selectedSprint?.endDate || getSessionValue('sprintEndDate'));
  const selectedSprintActualEndDate =
    formatSessionDate(
      jira.sprintCompleteDate?.completeDate
      || getSessionValue('sprintActualEndDate')
      || getSessionValue('sprintCompleteDate')
      || getSessionValue('actualSprintEndDate'),
    );

  return compactValue({
    ...ids,
    selectedValue,
    selectedValueLabel: jira.selectedValueLabel,
    estimationType: getSessionValue('velocityToggleType'),
    selectedProjectName: jira.selectedProjectName,
    selectedProjectId: jira.selectedProjectId,
    selectedSprintName: jira.selectedSprintName || selectedSprintFromList?.name || getSessionValue('sprintName'),
    selectedSprintId: jira.selectedSprintId,
    selectedSprintStartDate,
    selectedSprintEndDate,
    selectedSprintActualEndDate,
    selectedReleaseName: jira.selectedReleaseName || getSessionValue('releaseName') || releaseDashboardData.releaseName,
    selectedReleaseId: jira.selectedReleaseId || releaseDashboardMeta.releaseId,
    selectedDeveloperName: jira.selectedDeveloperName,
    selectedParameter: jira.selectedParameter,
    repositoryProvider: jira.repositoryProvider,
    organizationName: jira.selectedOrgName,
    organizationId: jira.selectedOrgId,
    boardList,
    sprintList,
    releasesList,
    releaseStartDate: getSessionValue('releaseStartDate') || releaseDashboardData.startDate,
    releaseEndDate: getSessionValue('releaseDate') || releaseDashboardData.targetedReleaseDate,
    targetedReleaseDate: releaseDashboardData.targetedReleaseDate,
    forecastedDate: releaseDashboardData.forecastedDate,
    releaseOverdue: releaseDashboardData.overdue,
    releaseStatus: releaseDashboardData.releaseStatus,
    riskAndAlert: releaseDashboardData.riskAndAlert,
    velocityData: jira.velocityData || velocityData,
    sprint: jira.Sprint,
    release: jira.Release,
    burndownVelocity: jira.burndownVelocity,
    burndownData: jira.burndownData,
    storyChurnData: jira.storyChurnData,
    standupBurndown: jira.standupBurndown,
    sprintCompleteDate: jira.sprintCompleteDate,
    cycleTimeData: jira.cycleTimeData,
    availableHours: jira.availableHours,
    taskCountData: jira.taskCountData,
    storyPointsData: jira.storyPointsData,
    statusCountData: jira.statusCountData,
    defectDensityData: jira.defectDensityData,
    defectRejectionData: jira.defectRejectionData,
    defectRemovalEfficiencyData: jira.defectRemovalEfficiencyData,
    costOfFixingDefectData: jira.costOfFixingDefectData,
    TimeToFixData: jira.TimeToFixData,
    bugClassificationData: jira.bugClassificationData,
    qaInsightsBugsData: jira.qaInsightsBugsData,
    qaInsightsTestsData: jira.qaInsightsTestsData,
    qaReferenceData: jira.qaReferenceData,
    jiraStatusByDeveloper: jira.jiraStatusByDeveloper,
    jiraTableData: jira.jiraTableData,
    releaseReadinessData: cxo.releaseReadinessData,
    releaseReadinessTrendsData: cxo.releaseReadinessTrendsData,
    actualStoryPoints: cxo.actualStoryPoints,
    dailyBurnup: cxo.dailyBurnup,
    releaseDashboard,
    releaseDashboardData,
    openPRs: git.openPRs,
    closedPRs: git.closedPRs,
    totalPRs: git.totalPRs,
    mergedWithoutReview: git.mergedWithoutReviewPRs,
    approvalRate: git.gitApprovalRateData,
    gitCycleTime: git.gitCycleTimeData,
    prSizeData: git.prSizeData,
    gitIterationTimeData: git.gitIterationTimeData,
    getDoraData: git.getDoraData,
    leadTime: git.leadTime,
    standupOpenPRs: git.standupOpenPRs,
    standupMergedPRsData: git.standupMergedPRsData,
    selectedRepo: git.selectedRepo,
    techQualitySnapshot,
    techQualitySelectedSeverity: techQuality?.selectedSeverity || 'All',
    techQualityData: techQuality?.data,
    techQualityLastFetchedFor: techQuality?.lastFetchedFor,
  });
};

const normalizeBackendResponse = (data, history) => createResponse(data?.answer || data?.message || '', {
  sessionId: data?.session_id,
  success: Boolean(data?.success),
  contextLength: Array.isArray(history) ? history.length : 0,
  source: 'qmetrix-ai',
  intent: data?.intent,
  dataSources: Array.isArray(data?.data_sources) ? data.data_sources : [],
});

const normalizeUserMessage = (message) => {
  if (typeof message === 'string') {
    return message;
  }

  if (message && typeof message === 'object') {
    return String(message.message || message.content || message.text || '').trim();
  }

  return String(message || '').trim();
};

export const fetchChatResponse = async ({ message = '', history = [], appState = {}, signal } = {}) => {
  const trimmedMessage = normalizeUserMessage(message);

  if (!trimmedMessage) {
    throw new Error('Please enter a message.');
  }

  try {
    const response = await axios.post(
      CHAT_ENDPOINT,
      {
        message: trimmedMessage,
        session_id: buildConversationSessionId(appState),
        companyId: getSessionValue('companyId') || appState?.user?.data?.companyId,
        projectId: getSessionValue('projectId') || appState?.jira?.selectedProjectId,
        boardId: resolveBoardId(appState),
        sprintId: getSessionValue('sprintId') || appState?.jira?.selectedSprintId,
        releaseId:
          getSessionValue('releaseId')
          || appState?.jira?.selectedReleaseId
          || appState?.releaseDashboard?.lastFetchedFor?.releaseId,
        repo: getSessionValue('repo') || appState?.jira?.repoList?.[0]?.name,
        dashboard_context: buildDashboardContext(appState),
      },
      {
        timeout: CHAT_TIMEOUT_MS,
        headers: buildRequestHeaders(),
        signal,
      },
    );

    return normalizeBackendResponse(response.data, history);
  } catch (error) {
    if (signal?.aborted || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
      throw error;
    }

    const backendMessage = error?.response?.data?.message || error?.response?.data?.detail;
    const timeoutMessage = error?.code === 'ECONNABORTED'
      ? 'QMetrix AI is still preparing the answer. Please retry in a moment.'
      : null;
    throw new Error(
      backendMessage || timeoutMessage || 'QMetrix AI service is not responding. Please make sure the AI backend is running on port 8000.',
    );
  }
};
